import { ReactElement, ReactNode } from "react";
import {
  Cell,
  Column,
  Header,
  HeaderGroup,
  Row,
  RowData,
  Table as ReactTable,
  flexRender,
  ColumnPinningPosition,
} from "@tanstack/react-table";
import {
  Table,
  TableBodyProps,
  TableCellProps,
  TableColumnHeaderProps,
  TableFooterProps,
  TableHeadProps,
  TableProps,
  TableRowProps,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { sum } from "lodash";
import ExpandableCellWrapper from "components/Shared/ChakraReactTable/ExpandableCellWrapper";
import TableLoadingSpinner, {
  TableLoadingSpinnerProps,
} from "./TableLoadingSpinner";
import { defaultRenderSortingEnabledHeaderContent } from "./SortControls";
import { pinnedColumnStyles } from "./utils";
import type { ColSpanCalculator } from "./useColSpan";

type ColumnProps = TableColumnHeaderProps | TableCellProps;

type PinningPosition = Exclude<ColumnPinningPosition, boolean>;

type PinnedDataAttributes = {
  /* eslint-disable @typescript-eslint/naming-convention */
  "data-pinning"?: PinningPosition;
  "data-pinning-index"?: number;
  "data-pinning-index-inside"?: number;
  "data-pinning-index-outside"?: number;
  /* eslint-enable @typescript-eslint/naming-convention */
};

type PinnedSizes = Record<PinningPosition, number[]>;

function getPinnedOffset(
  position: PinningPosition,
  pinnedIndex: number,
  pinnedSizes: PinnedSizes | null | undefined,
) {
  const sizes = pinnedSizes?.[position];

  if (!sizes) return 0;

  if (position === "left") {
    if (pinnedIndex === 0 || pinnedIndex >= sizes.length) return 0;
    return sum(sizes.slice(0, pinnedIndex));
  }

  if (position === "right") {
    if (pinnedIndex >= sizes.length - 1) return 0;
    return sum(sizes.slice(pinnedIndex + 1));
  }

  return 0;
}

function getHeaderOrCellProps<D extends RowData>(
  column: Column<D>,
  existing: ColumnProps,
  pinnedSizes?: PinnedSizes,
  colSpan: number = 1,
): ColumnProps & PinnedDataAttributes {
  const pinning = column.getIsPinned();

  if (!pinning) return existing;

  const sizes = pinnedSizes?.[pinning];
  const numPinned = sizes?.length || 0;
  const pinnedIndex = column.getPinnedIndex();
  const reverseIndex = numPinned - pinnedIndex - 1;

  // Adjust indices to account for colSpan
  const spanAdjustment = colSpan - 1;
  const adjustedReverseIndex = reverseIndex - spanAdjustment;
  const adjustedPinnedIndex = pinnedIndex + spanAdjustment;

  return {
    backgroundColor: "inherit",
    ...existing,
    "data-pinning": pinning,
    "data-pinning-index": pinnedIndex,
    "data-pinning-index-inside":
      pinning === "left"
        ? Math.max(0, adjustedReverseIndex)
        : Math.min(numPinned - 1, adjustedPinnedIndex),
    "data-pinning-index-outside":
      pinning === "left" ? pinnedIndex : reverseIndex,
    position: "sticky",
    zIndex: Math.max(
      1,
      typeof existing.zIndex === "number" ? existing.zIndex : 0,
    ),
    style: {
      ...existing?.style,
      [pinning === "left" ? "left" : "right"]: getPinnedOffset(
        pinning,
        pinnedIndex,
        pinnedSizes,
      ),
    },
  };
}

function getColumnSizes<D extends RowData>(columns: Column<D>[]) {
  return columns.map((col) => col.getSize());
}

function getPinnedColumnSizes<D extends RowData>(
  tableInstance: ReactTable<D>,
): PinnedSizes {
  const leftCols = tableInstance.getLeftVisibleLeafColumns();
  const rightCols = tableInstance.getRightVisibleLeafColumns();
  return { left: getColumnSizes(leftCols), right: getColumnSizes(rightCols) };
}

export type ColumnParams = {
  index: number;
  isFirst: boolean;
  isLast: boolean;
};

type RenderHeaderContent<D extends RowData> = (
  tableInstance: ReactTable<D>,
  header: Header<D, unknown>,
) => ReactNode;

export function makeDefaultRenderHeaderContent<D extends RowData>(
  renderSortingEnabledHeaderContent: RenderHeaderContent<D>,
) {
  return function renderHeaderContent(
    tableInstance: ReactTable<D>,
    header: Header<D, unknown>,
  ) {
    if (header.isPlaceholder) return null;

    if (tableInstance.options.enableSorting) {
      return renderSortingEnabledHeaderContent(tableInstance, header);
    }

    return flexRender(header.column.columnDef.header, header.getContext());
  };
}

const defaultRenderHeaderContent = makeDefaultRenderHeaderContent(
  defaultRenderSortingEnabledHeaderContent,
);

export type ComponentProps<D extends RowData> = {
  LoadingComponent?: React.ComponentType<{ tableInstance: ReactTable<D> }>;
  colgroupElement?: ReactElement;
  colSpanCalculator?: ColSpanCalculator<D>;
  getCellProps?: (
    cell: Cell<D, unknown>,
    columnParams: ColumnParams,
  ) => Partial<TableCellProps>;
  getColumnProps?: (
    column: Column<D>,
    columnParams: ColumnParams,
  ) => Partial<TableColumnHeaderProps | TableCellProps>;
  getFooterGroupProps?: (footerGroup: HeaderGroup<D>) => Partial<TableRowProps>;
  getFooterProps?: (
    footer: Header<D, unknown>,
    columnParams: ColumnParams,
  ) => Partial<TableCellProps>;
  getHeaderGroupProps?: (headerGroup: HeaderGroup<D>) => Partial<TableRowProps>;
  getHeaderProps?: (
    header: Header<D, unknown>,
    columnParams: ColumnParams,
  ) => Partial<TableColumnHeaderProps>;
  getRowProps?: (row: Row<D>) => Partial<TableRowProps> & {
    [dataAttribute: `data-${string}`]: unknown;
  };
  loadingSpinnerProps?: Omit<TableLoadingSpinnerProps<D>, "tableInstance">;
  renderHeaderContent?: RenderHeaderContent<D>;
  tableBodyProps?: TableBodyProps;
  tableFootProps?: TableFooterProps;
  tableHeadProps?: TableHeadProps;
  tableProps?: TableProps;
  noRowsPlaceholderProps?: Partial<TableCellProps>;
  noRowsPlaceholderRowProps?: Partial<TableRowProps>;
};

export type Props<D extends RowData> = ComponentProps<D> & {
  children?: React.ReactNode;
  hasPinEdgeShadow?: boolean;
  hasFooter?: boolean;
  isLoading?: boolean;
  noRowsPlaceholder?: React.ReactNode;
  tableInstance: ReactTable<D>;
};

export default function ChakraReactTable<D extends object>({
  LoadingComponent = TableLoadingSpinner,
  children,
  colgroupElement,
  colSpanCalculator,
  getCellProps,
  getColumnProps,
  getFooterGroupProps,
  getFooterProps,
  getHeaderGroupProps,
  getHeaderProps,
  getRowProps,
  hasFooter = false,
  hasPinEdgeShadow = true,
  isLoading = false,
  loadingSpinnerProps,
  noRowsPlaceholder,
  noRowsPlaceholderProps,
  noRowsPlaceholderRowProps,
  renderHeaderContent = defaultRenderHeaderContent,
  tableBodyProps,
  tableFootProps,
  tableHeadProps,
  tableInstance,
  tableProps,
}: Props<D>) {
  const { getHeaderGroups, getFooterGroups, getRowModel } = tableInstance;
  const { rows } = getRowModel();
  const numRows = rows?.length;
  const isPinned = tableInstance.getIsSomeColumnsPinned();
  const pinnedSizes = isPinned
    ? getPinnedColumnSizes(tableInstance)
    : undefined;
  const headerGroups = getHeaderGroups();
  return (
    <Table
      {...tableProps}
      sx={
        hasPinEdgeShadow
          ? { ...tableProps?.sx, ...pinnedColumnStyles }
          : tableProps?.sx
      }
    >
      {colgroupElement}
      <Thead {...tableHeadProps}>
        {headerGroups.map((headerGroup) => (
          <Tr
            key={headerGroup.id}
            data-depth={headerGroup.depth}
            data-depth-reverse={headerGroups.length - headerGroup.depth - 1}
            {...getHeaderGroupProps?.(headerGroup)}
          >
            {headerGroup.headers.map((header) => {
              const columnParams: ColumnParams = {
                index: header.index,
                isFirst: header.index === 0,
                isLast: header.index === headerGroup.headers.length - 1,
              };
              return (
                <Th
                  key={header.id}
                  colSpan={header.colSpan}
                  scope="col"
                  {...getHeaderOrCellProps(
                    header.column,
                    {
                      ...getColumnProps?.(header.column, columnParams),
                      ...header.column.columnDef.meta?.props,
                      ...header.column.columnDef.meta?.headerProps,
                      ...getHeaderProps?.(header, columnParams),
                    },
                    pinnedSizes,
                    header.colSpan,
                  )}
                >
                  {renderHeaderContent(tableInstance, header)}
                </Th>
              );
            })}
          </Tr>
        ))}
      </Thead>

      <Tbody
        {...tableBodyProps}
        backgroundColor={isPinned ? "inherit" : undefined}
      >
        {rows?.map((row) => {
          const rowCells = row.getVisibleCells();
          const skippedCells =
            colSpanCalculator?.getSkippedCellsInRow(rowCells) ?? new Set();

          return (
            <Tr
              key={row.id}
              data-depth={row.depth}
              data-index={row.index}
              data-expanded={row.getIsExpanded() || undefined}
              backgroundColor={isPinned ? "inherit" : undefined}
              {...getRowProps?.(row)}
            >
              {rowCells.map((cell, idx) => {
                if (skippedCells.has(cell.id)) return null;

                const renderLeafContent = () =>
                  flexRender(cell.column.columnDef.cell, cell.getContext());

                const isGrouped = cell.getIsGrouped();

                const renderContent = () => {
                  if (isGrouped) {
                    return (
                      <ExpandableCellWrapper row={cell.row}>
                        {renderLeafContent()}
                      </ExpandableCellWrapper>
                    );
                  }

                  if (cell.getIsAggregated()) {
                    return flexRender(
                      cell.column.columnDef.aggregatedCell ??
                        cell.column.columnDef.cell,
                      cell.getContext(),
                    );
                  }

                  if (cell.getIsPlaceholder()) {
                    return null;
                  }

                  return renderLeafContent();
                };

                const colSpan = colSpanCalculator?.getCellColSpan(cell) ?? 1;

                const columnParams: ColumnParams = {
                  index: idx,
                  isFirst: idx === 0,
                  isLast: idx === rowCells.length - 1,
                };

                return (
                  <Td
                    key={cell.id}
                    colSpan={colSpan > 1 ? colSpan : undefined}
                    data-grouped-cell={isGrouped || undefined}
                    {...getHeaderOrCellProps(
                      cell.column,
                      {
                        ...getColumnProps?.(cell.column, columnParams),
                        ...cell.column.columnDef.meta?.props,
                        ...cell.column.columnDef.meta?.cellProps,
                        ...getCellProps?.(cell, columnParams),
                        ...cell.column.columnDef.meta?.getCellProps?.(
                          cell,
                          columnParams,
                        ),
                      },
                      pinnedSizes,
                      colSpan,
                    )}
                  >
                    {renderContent()}
                  </Td>
                );
              })}
            </Tr>
          );
        })}

        {noRowsPlaceholder && !numRows && !isLoading && (
          <Tr {...noRowsPlaceholderRowProps}>
            <Td
              colSpan={tableInstance.getVisibleLeafColumns()?.length}
              textAlign="center"
              fontSize="sm"
              color="gray.400"
              background="linear-gradient(180deg, rgba(186, 194, 253, 0.15) 0%, rgba(186, 194, 253, 0.05) 33%, transparent 80%)"
              py={10}
              className="scroll-container-centered"
              {...noRowsPlaceholderProps}
            >
              {noRowsPlaceholder}
            </Td>
          </Tr>
        )}

        {isLoading &&
          !numRows &&
          (LoadingComponent === TableLoadingSpinner ? (
            <TableLoadingSpinner
              tableInstance={tableInstance}
              {...loadingSpinnerProps}
            />
          ) : (
            <LoadingComponent tableInstance={tableInstance} />
          ))}

        {children}
      </Tbody>

      {hasFooter && (
        <Tfoot {...tableFootProps}>
          {getFooterGroups().map((footerGroup) => (
            <Tr
              key={footerGroup.id}
              backgroundColor={isPinned ? "inherit" : undefined}
              {...getFooterGroupProps?.(footerGroup)}
            >
              {footerGroup.headers.map((footer) => {
                const columnParams: ColumnParams = {
                  index: footer.index,
                  isFirst: footer.index === 0,
                  isLast: footer.index === footerGroup.headers.length - 1,
                };
                return (
                  <Td
                    key={footer.id}
                    colSpan={footer.colSpan}
                    {...getHeaderOrCellProps(
                      footer.column,
                      {
                        ...getColumnProps?.(footer.column, columnParams),
                        ...footer.column.columnDef.meta?.props,
                        ...footer.column.columnDef.meta?.footerProps,
                        ...getFooterProps?.(footer, columnParams),
                      },
                      pinnedSizes,
                      footer.colSpan,
                    )}
                  >
                    {!footer.isPlaceholder &&
                      flexRender(
                        footer.column.columnDef.footer,
                        footer.getContext(),
                      )}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tfoot>
      )}
    </Table>
  );
}
